import uniqBy from "lodash.uniqby";
import { getExtension } from "utils";

import { FileUploadResult } from ".";

export interface FileUploadState {
  file: File;
  state:
    | { kind: "uploading" | "ready" | "uploaded" }
    | { kind: "error" | "upload-error"; message: string };
}

export const unknownFileType = "Unknown file type";
export const unsupportedFileType = "Unsupported file type";
export const fileTooLarge = "File is too large";

export const getMessage = (f: FileUploadState): string | undefined =>
  f.state.kind === "error" || f.state.kind === "upload-error"
    ? f.state.message
    : undefined;

export const fromFile = (
  file: File,
  allowedExtensions: Set<string>,
  maxSize: number
): FileUploadState => {
  const ext = getExtension(file.name);

  const state = ((): FileUploadState["state"] => {
    if (ext === undefined) {
      return { kind: "error", message: unknownFileType };
    } else if (!allowedExtensions.has(ext.toLowerCase())) {
      return { kind: "error", message: unsupportedFileType };
    } else if (file.size > maxSize) {
      return { kind: "error", message: fileTooLarge };
    } else {
      return { kind: "ready" };
    }
  })();

  return { file, state };
};

export const addFiles = (
  current: FileUploadState[],
  files: File[],
  allowedExtensions: Set<string>,
  maxFiles: number,
  maxSize: number
): FileUploadState[] =>
  // Reversing the list before doing the uniqBy will ensure that
  // later items overwrite new items
  uniqBy(
    [
      ...current,
      ...files.map((file) => fromFile(file, allowedExtensions, maxSize)),
    ].reverse(),
    (f) => f.file.name
  )
    .reverse()
    .slice(0, maxFiles);

export const removeFile = (
  current: FileUploadState[],
  file: File
): FileUploadState[] => current.filter((c) => c.file !== file);

export const setUploading = (current: FileUploadState[]): FileUploadState[] =>
  current.map((f) =>
    f.state.kind === "ready" ? { ...f, state: { kind: "uploading" } } : f
  );

const mapResult = (result: FileUploadResult): FileUploadState["state"] => {
  if (result.result === "error") {
    return { kind: "upload-error", message: "Error uploading" };
  } else {
    return { kind: "uploaded" };
  }
};

export const applyUploadResult = (
  current: FileUploadState[],
  result: FileUploadResult
): FileUploadState[] =>
  current.map((f) =>
    f.file === result.file ? { ...f, state: mapResult(result) } : f
  );

export const canUpload = (current: FileUploadState[]): boolean =>
  current.length > 0 &&
  current.every((a) => a.state.kind === "ready" || a.state.kind === "uploaded");

export const isUploading = (current: FileUploadState[]): boolean =>
  current.length > 0 && current.some((a) => a.state.kind === "uploading");

export const isComplete = (current: FileUploadState[]): boolean =>
  current.length > 0 && current.every((a) => a.state.kind === "uploaded");

export const hasUploadErrors = (current: FileUploadState[]): boolean =>
  current.length > 0 && current.some((a) => a.state.kind === "upload-error");
